// Render del cuerpo de una nota SENCILLA troceándolo en texto e imágenes
// inline. Cada token `![alt](attachment:filename)` cuyo filename case con un
// adjunto persistido (y sea imagen) se renderiza como <img>; los que no casen
// caen a texto (fallback inocuo). Réplica del NoteBody del CRM
// (crm/apps/web/src/shared/notes/NoteBody.tsx), compartido por las notas de
// ticket (TicketDetail) y las notas internas del timeline del lead — cada
// superficie trae su propio `fetchObjectUrl` (los adjuntos viven en endpoints
// distintos: /tickets/... y /leads/.../comments/...).

import { useEffect, useState } from "react";
import { inlineImageToken, parseNoteBody } from "../../api/crmInlineImages";
import { TicketAttachment } from "../../api/crmTypes";

export type FetchAttachmentObjectUrl = (
  attachmentId: string,
) => Promise<string>;

export default function NoteBody({
  body,
  attachments,
  fetchObjectUrl,
  style,
}: {
  body: string;
  attachments: TicketAttachment[];
  /** Descarga autenticada del blob → objectURL. DEBE ser estable (useCallback
   *  en el caller); si cambia por render, bucle de descargas. */
  fetchObjectUrl: FetchAttachmentObjectUrl;
  style?: React.CSSProperties;
}) {
  const parts = parseNoteBody(body);
  return (
    <div
      style={{
        whiteSpace: "pre-wrap",
        fontSize: ".85rem",
        marginTop: "0.2rem",
        wordBreak: "break-word",
        ...style,
      }}
    >
      {parts.map((part, i) => {
        if (part.type === "text") return <span key={i}>{part.value}</span>;
        const att = attachments.find((a) => a.filename === part.filename);
        if (!att || !att.contentType.startsWith("image/")) {
          // Token sin adjunto que case (o no-imagen): render textual inocuo.
          return (
            <span key={i}>
              {inlineImageToken(part.filename ?? "", part.value)}
            </span>
          );
        }
        return (
          <InlineNoteImage
            key={i}
            attachment={att}
            alt={part.value}
            fetchObjectUrl={fetchObjectUrl}
          />
        );
      })}
    </div>
  );
}

// Imagen inline: obtiene el blob autenticado (fetch+objectURL, cookie via
// credentials), lo revoca al desmontar, y permite abrirla a tamaño completo en
// una pestaña nueva. Placeholder gris mientras carga; texto de error si falla.
export function InlineNoteImage({
  attachment,
  alt,
  fetchObjectUrl,
}: {
  attachment: TicketAttachment;
  alt: string;
  fetchObjectUrl: FetchAttachmentObjectUrl;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    fetchObjectUrl(attachment.id)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setSrc(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.id, fetchObjectUrl]);

  if (failed) {
    return (
      <span style={{ fontSize: ".76rem", color: "#b32424" }}>
        [No se pudo cargar la imagen: {attachment.filename}]
      </span>
    );
  }
  if (!src) {
    return (
      <span
        style={{
          display: "inline-block",
          width: 160,
          height: 90,
          borderRadius: "0.4rem",
          background: "#e9ecef",
          verticalAlign: "middle",
          margin: "0.25rem 0",
        }}
        title="Cargando imagen…"
        aria-label="Cargando imagen…"
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onClick={() => window.open(src, "_blank", "noopener")}
      style={{
        display: "block",
        margin: "0.25rem 0",
        maxWidth: "100%",
        maxHeight: 320,
        borderRadius: "0.4rem",
        border: "1px solid #dee2e6",
        cursor: "zoom-in",
      }}
      title="Abrir a tamaño completo"
    />
  );
}
